import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import cn from '@components/utils/cn'

import { Button } from '@atoms'
import Badge from '@components/atoms/Badge'
import { Stat } from '@atoms/Stat'
import { SelectField } from '@components/molecules/SelectField'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import { SegmentedControl, type SegmentedOption } from '@components/atoms/SegmentedControl'
import { ColumnVisibilityDropdown } from '@components/molecules/ColumnVisibilityDropdown'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { useColumnVisibility } from '@hooks/useColumnVisibility'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { DataTableRoot, DataTableContent, DataTableFooter, SortableHeader } from '@components/molecules/DataTable'
import EmptyState from '@components/molecules/EmptyState'
import { PageLayout } from '@components/layouts/PageLayout'
import Icon, { type IconTypes } from '@components/utils/Icon'

type Status = 'draft' | 'awaiting approval' | 'approved' | 'scheduled' | 'sent' | 'failed'
type Channel = 'email' | 'sms'
type Layout = 'table' | 'cards'

type Blast = {
  id: number
  title: string
  body: string
  group: string
  groupId: number
  recipients: number | null
  status: Status
  delivered?: number
  openRate?: number
  error?: string
  audience?:
    | { kind: 'all_members'; filters?: string[] }
    | { kind: 'all_rsvps'; eventName: string; eventArchived?: boolean; rsvpType?: RsvpType }
  undelivered?: number
  replies?: number
  testSent?: boolean
  includeShiftInfo?: boolean
  shiftDate?: string
  missingShiftVariable?: boolean
  date: string
  time?: string
  sortKey: number
}

type RsvpType = 'attendees' | 'non_attendees' | 'all_rsvps'

const RSVP_TYPE_LABEL: Record<RsvpType, string> = {
  attendees: 'Attendees',
  non_attendees: 'Non-attendees',
  all_rsvps: 'All RSVPs',
}

const STATUS_TONE: Record<Status, 'muted' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'muted',
  'awaiting approval': 'warning',
  approved: 'info',
  scheduled: 'info',
  sent: 'success',
  failed: 'error',
}

const STATUS_LABEL: Record<Status, string> = {
  draft: 'Draft',
  'awaiting approval': 'Awaiting approval',
  approved: 'Approved',
  scheduled: 'Scheduled',
  sent: 'Sent',
  failed: 'Failed',
}

const IN_FLIGHT: Status = 'approved'

const STATUS_ICON: Record<Status, IconTypes> = {
  draft: 'file-05',
  'awaiting approval': 'clock',
  approved: 'check',
  scheduled: 'calendar',
  sent: 'check-circle',
  failed: 'alert-circle',
}

const STATUS_ORDER: Status[] = ['draft', 'awaiting approval', 'approved', 'scheduled', 'sent', 'failed']

const PAGE_SIZE = 12

const GROUPS = [
  { id: 1, name: 'Warringah Branch' },
  { id: 2, name: 'Northern Beaches' },
  { id: 3, name: 'Pittwater and Northern Peninsula Branch Committee' },
]

const EMAIL_BLASTS: Blast[] = [
  {
    id: 1,
    title: 'Monthly newsletter — September',
    body: 'Hi %{first_name}, here is what the branch got up to in September. We doorknocked 412 homes, signed up 38 new volunteers and the community garden finally has its water tank.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 1240,
    delivered: 1231,
    openRate: 62,
    status: 'sent',
    date: '12 Sep 2026',
    sortKey: 20260912,
  },
  {
    id: 2,
    title: 'Doorknock reminder',
    body: "Don't forget, we are doorknocking this Saturday from 9am. Meet at the community hall on Pittwater Road and bring a hat.",
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 86,
    status: 'scheduled',
    date: '21 Sep 2026',
    time: '9:00am',
    sortKey: 20260921,
  },
  {
    id: 3,
    title: 'Volunteer thank-you',
    body: 'Thanks so much for giving up your Saturday. We could not have covered the whole north end without you, and the feedback from residents was lovely.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: null,
    status: 'draft',
    date: '8 Sep 2026',
    sortKey: 20260908,
  },
  {
    id: 4,
    title: 'Fundraising push before the deadline',
    body: 'We are $4,000 short of the target with a week to go. If every person reading this chipped in $20 we would be there by Friday.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 310,
    status: 'awaiting approval',
    date: '7 Sep 2026',
    sortKey: 20260907,
  },
  {
    id: 5,
    title: 'Branch AGM notice',
    body: 'The branch AGM is on the 20th at 8am, upstairs at the surf club. Nominations for the committee close a week beforehand.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 75,
    status: 'approved',
    date: '20 Sep 2026',
    time: '8:00am',
    sortKey: 20260920,
  },
  {
    id: 6,
    title: 'Bounced campaign',
    body: 'A quick update on the council submission and what happens next in the planning process.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 12,
    status: 'failed',
    error: 'SendGrid rejected the sender address',
    date: '3 Sep 2026',
    sortKey: 20260903,
  },
  {
    id: 7,
    title:
      'A very long subject line that keeps going well past what any row can reasonably show before it has to truncate somewhere sensible',
    body: 'Short body, very long subject — this row exists to check that the subject truncates without pushing the snippet or the badge around.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: null,
    status: 'draft',
    date: '2 Sep 2026',
    sortKey: 20260902,
  },
  {
    id: 8,
    title: 'August wrap-up',
    body: 'August in review: two street stalls, one council deputation and a new record for petition signatures in a single weekend.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 1198,
    delivered: 1180,
    openRate: 48,
    status: 'sent',
    date: '29 Aug 2026',
    sortKey: 20260829,
  },
  {
    id: 9,
    title: 'Rally callout',
    body: 'Buses leave from the station at 8am sharp on Saturday. Reply to this email if you need a seat and we will put your name down.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 980,
    delivered: 964,
    openRate: 71,
    status: 'sent',
    date: '21 Aug 2026',
    sortKey: 20260821,
  },
  {
    id: 10,
    title: 'Sent moments ago, stats not in yet',
    body: 'This one went out in the last few minutes, so the delivery report has not come back from SendGrid yet.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 64,
    status: 'sent',
    date: '18 Sep 2026',
    sortKey: 20260918,
  },

  {
    id: 17,
    title:
      'Reminder about the upcoming community consultation session on the proposed rezoning of the foreshore precinct and what it means for local residents',
    body: 'Long subject, short body. This row exists to see where a genuinely long subject gives up and what that does to the columns beside it.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 512,
    delivered: 505,
    openRate: 44,
    status: 'sent',
    date: '17 Sep 2026',
    sortKey: 20260917,
  },
  {
    id: 18,
    title: 'SupporterBaseCommunityConsultationRezoningForeshorePrecinctResidentsNewsletterSeptemberTwentyTwentySix',
    body: 'One unbroken word as the subject — the worst case for truncation, since there is nowhere sensible to break.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 88,
    status: 'draft',
    date: '16 Sep 2026',
    sortKey: 20260916,
  },
  {
    id: 19,
    title:
      'Both the subject and the body run long here, so we can see the two truncations stacked on top of one another in the same cell',
    body: 'And the body is long too, which is the common real-world case: a newsletter with a descriptive subject line and an opening paragraph that carries on well past the width of any column we are ever going to give it, so both lines have to give up somewhere.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 1402,
    delivered: 1388,
    openRate: 58,
    status: 'sent',
    date: '15 Sep 2026',
    sortKey: 20260915,
  },
  {
    id: 20,
    title:
      'Long subject on a scheduled blast, to check the Scheduled for column still fits when the subject is doing its best to take the whole row',
    body: 'Short body.',
    group: 'Pittwater and Northern Peninsula Branch Committee',
    groupId: 3,
    recipients: 240,
    status: 'scheduled',
    date: '26 Sep 2026',
    time: '6:45pm',
    sortKey: 20260926,
  },

  {
    id: 11,
    title: 'Weekly update #12',
    body: 'Twelfth week running. Nominations for the community award close Friday and we still need two people for the Sunday shift.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 329,
    delivered: 325,
    openRate: 55,
    status: 'sent',
    date: '11 Sep 2026',
    sortKey: 20260911,
  },
  {
    id: 12,
    title: 'Weekly update #11',
    body: 'Eleventh week. The council has pushed the decision back a fortnight, which gives us more time to collect signatures.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 318,
    delivered: 311,
    openRate: 51,
    status: 'sent',
    date: '4 Sep 2026',
    sortKey: 20260904,
  },
  {
    id: 13,
    title: 'Beach cleanup reminder',
    body: 'Gloves and bags provided. Meet at the northern car park at 7:30am, and we should be done well before the heat sets in.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 142,
    status: 'scheduled',
    date: '24 Sep 2026',
    time: '7:30am',
    sortKey: 20260924,
  },
  {
    id: 14,
    title: 'Draft: spring campaign',
    body: 'Rough outline only. Three asks, a petition link and a save-the-date for the launch event in October.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: null,
    status: 'draft',
    date: '5 Sep 2026',
    sortKey: 20260905,
  },
  {
    id: 15,
    title: 'Pittwater quarterly update',
    body: "Quarterly round-up for the Pittwater branch, including the treasurer's report and dates for the next three meetings.",
    group: 'Pittwater and Northern Peninsula Branch Committee',
    groupId: 3,
    recipients: 410,
    delivered: 402,
    openRate: 39,
    status: 'sent',
    date: '9 Sep 2026',
    sortKey: 20260909,
  },
  {
    id: 16,
    title: 'Needs sign-off: budget appeal',
    body: 'Draft appeal for the end of financial year. Needs a second pair of eyes on the numbers before it goes anywhere.',
    group: 'Pittwater and Northern Peninsula Branch Committee',
    groupId: 3,
    recipients: 88,
    status: 'awaiting approval',
    date: '6 Sep 2026',
    sortKey: 20260906,
  },
]

const SMS_BLASTS: Blast[] = [
  {
    id: 101,
    title: '',
    body: 'Doorknock this Saturday, 9am at the community hall on Pittwater Road. Bring a hat. Reply STOP to opt out.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 640,
    delivered: 631,
    status: 'sent',
    date: '14 Sep 2026',
    sortKey: 20260914,
  },
  {
    id: 102,
    title: '',
    body: 'Reminder: branch meeting tonight at 5:30pm upstairs at the surf club.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 95,
    status: 'scheduled',
    date: '21 Sep 2026',
    time: '5:30pm',
    sortKey: 20260921,
  },
  {
    id: 103,
    title: '',
    body: 'Thanks for volunteering on Saturday! We covered the whole north end because of you.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: null,
    status: 'draft',
    date: '9 Sep 2026',
    sortKey: 20260909,
  },
  {
    id: 104,
    title: '',
    body: 'Emoji check 🎉 ✅ 📣 for rendering, plus a longer tail so we can see how two lines of message body sit against the metadata line underneath.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 150,
    delivered: 148,
    status: 'sent',
    date: '27 Aug 2026',
    sortKey: 20260827,
  },
  {
    id: 105,
    title: '',
    body: 'Polling place reminder for Saturday — booths open 8am.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 8,
    status: 'failed',
    error: 'Twilio rejected 8 numbers as unreachable',
    date: '1 Sep 2026',
    sortKey: 20260901,
  },
  {
    id: 109,
    title: '',
    body: 'A deliberately long SMS that runs well past a single segment so we can see how two clamped lines of message body behave in the subject column, including where the clamp lands and whether the row height stays even with its neighbours. Reply STOP to opt out.',
    group: 'Warringah Branch',
    groupId: 1,
    recipients: 430,
    delivered: 421,
    status: 'sent',
    date: '16 Sep 2026',
    sortKey: 20260916,
  },
  {
    id: 110,
    title: '',
    body: 'ShortOne',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: null,
    status: 'draft',
    date: '15 Sep 2026',
    sortKey: 20260915,
  },

  {
    id: 106,
    title: '',
    body: 'Awaiting sign-off on this one before it goes to the full list.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 210,
    status: 'awaiting approval',
    date: '6 Sep 2026',
    sortKey: 20260906,
  },
  {
    id: 107,
    title: '',
    body: 'Beach cleanup is on, see you at 8 at the northern car park.',
    group: 'Northern Beaches',
    groupId: 2,
    recipients: 142,
    delivered: 139,
    status: 'sent',
    date: '3 Sep 2026',
    sortKey: 20260903,
  },
  {
    id: 108,
    title: '',
    body: 'Roster reminder for Saturday. Check your shift time in the app.',
    group: 'Pittwater and Northern Peninsula Branch Committee',
    groupId: 3,
    recipients: 77,
    delivered: 74,
    status: 'sent',
    date: '2 Sep 2026',
    sortKey: 20260902,
  },
]

const EXTRAS: Record<number, Partial<Blast>> = {
  1: { audience: { kind: 'all_members' }, undelivered: 9, replies: 4 },
  2: {
    audience: { kind: 'all_rsvps', eventName: 'Spring doorknock', rsvpType: 'attendees' },
    includeShiftInfo: true,
    shiftDate: '21 Sep 2026',
  },
  3: { audience: { kind: 'all_members' }, testSent: true },
  4: { audience: { kind: 'all_members', filters: ['Highly engaged'] } },
  20: { audience: { kind: 'all_rsvps', eventName: 'Spring doorknock' }, includeShiftInfo: true },
  5: {
    audience: { kind: 'all_rsvps', eventName: 'Autumn street stall', eventArchived: true, rsvpType: 'non_attendees' },
  },
  6: { audience: { kind: 'all_members' } },
  7: { audience: { kind: 'all_members' }, missingShiftVariable: true },
  8: { audience: { kind: 'all_members', filters: ['Highly engaged', 'Volunteer'] }, undelivered: 18, replies: 1 },
  9: {
    audience: { kind: 'all_rsvps', eventName: 'Winter rally', rsvpType: 'all_rsvps' },
    undelivered: 16,
    replies: 12,
  },
  10: { audience: { kind: 'all_members' } },
  11: {
    audience: {
      kind: 'all_members',
      filters: ['Newsletter subscribers', 'Not recently contacted', 'Advanced filter: lapsed donors'],
    },
    includeShiftInfo: true,
    undelivered: 4,
    replies: 2,
  },
  12: { audience: { kind: 'all_members' }, undelivered: 7 },
  13: { audience: { kind: 'all_rsvps', eventName: 'Beach cleanup' } },
  14: { audience: { kind: 'all_members' }, testSent: true },
  15: {
    audience: { kind: 'all_members', filters: ['Advanced filter: postcode 2100-2108'] },
    undelivered: 8,
    replies: 3,
  },
  16: { audience: { kind: 'all_members' } },

  17: {
    audience: {
      kind: 'all_rsvps',
      eventName: 'Foreshore rezoning consultation session for local residents',
      rsvpType: 'attendees',
    },
    undelivered: 7,
    replies: 9,
  },
  18: { audience: { kind: 'all_members' } },
  19: {
    audience: {
      kind: 'all_members',
      filters: ['Highly engaged', 'Volunteer', 'Advanced filter: active in last 90 days'],
    },
    undelivered: 14,
    replies: 21,
  },

  101: { audience: { kind: 'all_members' }, undelivered: 9 },
  102: { audience: { kind: 'all_rsvps', eventName: 'Branch meeting' } },
  103: { audience: { kind: 'all_members' }, testSent: true },
  104: { audience: { kind: 'all_members', filters: ['Volunteer'] }, undelivered: 2 },
  105: { audience: { kind: 'all_members' } },
  106: { audience: { kind: 'all_members' } },
  107: { audience: { kind: 'all_rsvps', eventName: 'Beach cleanup', rsvpType: 'non_attendees' }, undelivered: 3 },
  108: { audience: { kind: 'all_members' }, undelivered: 3 },
  109: { audience: { kind: 'all_members' }, undelivered: 9 },
  110: { audience: { kind: 'all_members' } },
}

const withExtras = (blasts: Blast[]): Blast[] => blasts.map((b) => ({ ...b, ...EXTRAS[b.id] }))

type AudienceSummary = { label: string; filters: string[]; shiftNote: string | null }

function shiftNoteFor(blast: Blast): string | null {
  if (!blast.includeShiftInfo) return null
  return blast.shiftDate ? `shifts on ${blast.shiftDate} only` : 'with assigned shifts only'
}

function audienceSummary(blast: Blast): AudienceSummary | null {
  if (!blast.audience) return null

  if (blast.audience.kind === 'all_rsvps') {
    const event = blast.audience.eventArchived ? `${blast.audience.eventName} (archived)` : blast.audience.eventName
    return {
      label: withShiftNote(`${RSVP_TYPE_LABEL[blast.audience.rsvpType ?? 'all_rsvps']} · ${event}`, blast),
      filters: [],
      shiftNote: shiftNoteFor(blast),
    }
  }

  return {
    label: withShiftNote('All members', blast),
    filters: blast.audience.filters ?? [],
    shiftNote: shiftNoteFor(blast),
  }
}

function withShiftNote(base: string, blast: Blast): string {
  const note = shiftNoteFor(blast)
  return note ? `${base}, ${note}` : base
}

function AudienceCell({ blast }: { blast: Blast }) {
  const summary = audienceSummary(blast)
  if (!summary) return <span className="tw:text-gray-true-500 tw:italic">Not set</span>

  if (summary.filters.length === 0) {
    return <span className="tw:block tw:truncate tw:text-gray-700">{summary.label}</span>
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
          <span className="tw:truncate tw:text-gray-700">{summary.label}</span>
          <Badge color="muted">{`+${summary.filters.length}`}</Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="tw:max-w-xs">{`Narrowed by ${summary.filters.join(', ')}`}</TooltipContent>
    </Tooltip>
  )
}

function isEditable(blast: Blast): boolean {
  return !['sent', 'approved', 'scheduled'].includes(blast.status)
}

function isDeletable(blast: Blast): boolean {
  return !['sent', 'scheduled', 'approved'].includes(blast.status)
}

function isCancellable(blast: Blast): boolean {
  return blast.status === 'scheduled'
}

function isSubmittable(blast: Blast): boolean {
  return !['sent', 'scheduled', 'approved', 'awaiting approval'].includes(blast.status)
}

function submitReason(blast: Blast): string {
  if (blast.status === 'awaiting approval') return 'This blast is already awaiting approval'
  return unavailableReason(blast)
}

function unavailableReason(blast: Blast): string {
  if (blast.status === 'sent') return 'This blast has already been sent'
  if (blast.status === 'approved') return 'This blast is being sent now'
  if (blast.status === 'scheduled') return 'Cancel the scheduled send first'
  return ''
}

function ActionButton({
  label,
  icon,
  enabled,
  reason,
  rank = 'secondary',
  color,
}: {
  label: string
  icon: IconTypes
  enabled: boolean
  reason: string
  rank?: 'primary' | 'secondary'
  color?: 'primary' | 'danger'
}) {
  const button = (
    <Button rank={rank} color={color} size="sm" leadingIcon={icon} disabled={!enabled} ariaLabel={label}>
      {label}
    </Button>
  )

  if (enabled) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tw:inline-flex" tabIndex={0} aria-label={`${label}. ${reason}`}>
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  )
}

function StatusBadge({ status, detail }: { status: Status; detail?: string }) {
  return (
    <Badge color={STATUS_TONE[status]} leadingIcon={STATUS_ICON[status]} className="tw:whitespace-nowrap">
      {detail ? `${STATUS_LABEL[status]} ${detail}` : STATUS_LABEL[status]}
    </Badge>
  )
}

function statusDetail(blast: Blast): string | undefined {
  if (blast.status === 'sent') return blast.date
  if (blast.status === 'scheduled') return `${blast.date}${blast.time ? `, ${blast.time}` : ''}`
  return undefined
}

function SubjectCell({ blast, channel }: { blast: Blast; channel: Channel }) {
  return (
    <button className="tw:block tw:w-full tw:max-w-md tw:text-left">
      <span
        className={cn(
          'tw:text-sm tw:font-semibold tw:text-gray-900 tw:hover:text-primary-700',
          channel === 'sms' ? 'tw:line-clamp-2 tw:block' : 'tw:block tw:truncate'
        )}
      >
        {channel === 'email' ? blast.title : blast.body}
      </span>
    </button>
  )
}

function HeaderWithHint({ hint, children }: { hint: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tw:inline-flex tw:items-center tw:gap-1">
          {children}
          <Icon type="alert-circle" width={14} height={14} className="tw:text-gray-400" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent className="tw:max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  )
}

function GroupCell({ blast }: { blast: Blast }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tw:block tw:max-w-40 tw:truncate">
          <GroupLink blast={blast} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{blast.group}</TooltipContent>
    </Tooltip>
  )
}

function GroupLink({ blast }: { blast: Blast }) {
  return (
    <a
      href={`/action_groups/${blast.groupId}`}
      onClick={(e) => e.preventDefault()}
      className="tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current"
    >
      {blast.group}
    </a>
  )
}

function RowActions({ blast }: { blast: Blast }) {
  const label = blast.title || 'this blast'

  return (
    <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
      {isEditable(blast) && (
        <Button rank="secondary" size="sm" leadingIcon="pencil-01" ariaLabel={`Edit ${label}`}>
          <span className="tw:hidden tw:sm:inline">Edit</span>
        </Button>
      )}
      <Button rank="secondary" size="sm" leadingIcon="eye" ariaLabel={`View ${label}`}>
        <span className="tw:hidden tw:sm:inline">View</span>
      </Button>
    </div>
  )
}

function deliveryValue(blast: Blast, channel: Channel): string | null {
  if (blast.delivered === undefined) return null
  const delivered = `${blast.delivered.toLocaleString()} delivered`
  if (channel === 'email' && blast.openRate !== undefined) return `${delivered} · ${blast.openRate}% opened`
  return delivered
}

function BlastCard({
  blast,
  channel,
  showGroup,
  canApprove,
}: {
  blast: Blast
  channel: Channel
  showGroup: boolean
  canApprove: boolean
}) {
  const summary = audienceSummary(blast)
  const reason = unavailableReason(blast)

  return (
    <li className="tw:flex tw:w-full tw:flex-col tw:gap-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:shadow-xs tw:sm:p-6">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-x-6 tw:gap-y-3">
        <h2 className="tw:m-0 tw:min-w-0 tw:flex-1 tw:wrap-break-word tw:text-lg tw:font-semibold tw:text-gray-900 tw:hover:text-primary-700">
          <a href="#" onClick={(e) => e.preventDefault()}>
            {channel === 'email' ? blast.title : blast.body}
          </a>
        </h2>
        <StatusBadge status={blast.status} detail={statusDetail(blast)} />
      </div>

      <dl className="tw:m-0 tw:grid tw:grid-cols-2 tw:gap-x-6 tw:gap-y-4 tw:sm:grid-cols-3">
        {showGroup && (
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
            <dt className="tw:inline-flex tw:items-end tw:gap-1.5 tw:text-xs tw:font-bold tw:uppercase tw:leading-[18px] tw:text-gray-true-500">
              <Icon type="users-01" width={20} height={20} className="tw:shrink-0 tw:text-gray-true-500" aria-hidden />
              Group
            </dt>
            <dd className="tw:m-0 tw:min-w-0 tw:wrap-break-word tw:text-sm">
              <a
                href={`/action_groups/${blast.groupId}`}
                onClick={(e) => e.preventDefault()}
                className="tw:border-b tw:border-transparent tw:font-medium tw:text-primary-700 tw:hover:border-current tw:hover:text-primary-900"
              >
                {blast.group}
              </a>
            </dd>
          </div>
        )}

        <Stat icon="filter-lines" label="Audience" value={summary?.label ?? null} fallbackValue="Not set" />

        <Stat
          icon="users-01"
          label="Recipients"
          value={blast.recipients === null ? null : blast.recipients.toLocaleString()}
          fallbackValue="Not built yet"
        />

        <Stat
          icon="check-circle"
          label="Delivery"
          value={deliveryValue(blast, channel)}
          fallbackValue={blast.status === 'sent' ? 'Not reported yet' : 'Not sent'}
        />

        <Stat icon="clock" label="Created" value={blast.date} />
      </dl>

      <div className="tw:w-full tw:border-t tw:border-gray-200" />

      <div className="tw:flex tw:flex-wrap tw:gap-3 tw:sm:justify-between">
        <Button rank="primary" size="sm" leadingIcon="eye">
          View
        </Button>

        <span className="tw:inline-flex tw:flex-wrap tw:gap-3">
          {isCancellable(blast) && <ActionButton label="Cancel send" icon="x-circle" enabled reason="" />}
          {!canApprove && (
            <ActionButton
              label="Submit for approval"
              icon="check"
              enabled={isSubmittable(blast)}
              reason={submitReason(blast)}
            />
          )}
          <ActionButton label="Edit" icon="pencil-01" enabled={isEditable(blast)} reason={reason} />
          <ActionButton label="Delete" icon="trash-01" color="danger" enabled={isDeletable(blast)} reason={reason} />
        </span>
      </div>
    </li>
  )
}

export default function BlastsIndex() {
  const [channel, setChannel] = useState<Channel>('email')
  const [leadsManyGroups, setLeadsManyGroups] = useState(true)
  const [canApprove, setCanApprove] = useState(false)
  const [layout, setLayout] = useLocalStorage<Layout>('viewMode:blastsIndex', 'table')
  const [showDesign, setShowDesign] = useState(false)

  const [search, setSearch] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [group, setGroup] = useState<string | undefined>(undefined)
  const [sorting, setSorting] = useState<SortingState>([])
  const { columnVisibility, setColumnVisibility } = useColumnVisibility('blastsIndex', {
    Replies: false,
    'Scheduled for': false,
  })
  const [page, setPage] = useState(1)

  const source = useMemo(() => withExtras(channel === 'email' ? EMAIL_BLASTS : SMS_BLASTS), [channel])
  const noun = channel === 'email' ? 'email blast' : 'SMS blast'

  const visible = useMemo(
    () => (leadsManyGroups ? source : source.filter((b) => b.groupId === GROUPS[0].id)),
    [source, leadsManyGroups]
  )

  const blasts = useMemo(() => {
    const term = search.trim().toLowerCase()

    return visible
      .filter((blast) => {
        if (statuses.length > 0 && !statuses.includes(blast.status)) return false
        if (group && blast.group !== group) return false
        if (term && !`${blast.title} ${blast.body} ${blast.group}`.toLowerCase().includes(term)) return false
        return true
      })
      .sort((a, b) => b.sortKey - a.sortKey)
  }, [visible, search, statuses, group])

  const totalPages = Math.ceil(blasts.length / PAGE_SIZE)
  const pagedBlasts = useMemo(
    () => (totalPages > 1 ? blasts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : blasts),
    [blasts, page, totalPages]
  )

  const columns = useMemo<ColumnDef<Blast>[]>(() => {
    const cols: ColumnDef<Blast>[] = [
      {
        id: 'Subject',
        accessorKey: 'title',
        header: ({ column }) => (
          <SortableHeader column={column}>{channel === 'email' ? 'Subject' : 'Message'}</SortableHeader>
        ),
        cell: ({ row }) => <SubjectCell blast={row.original} channel={channel} />,
        minSize: 260,
        meta: { allowWrap: true },
      },
    ]

    if (leadsManyGroups) {
      cols.push({
        id: 'Group',
        accessorKey: 'group',
        header: ({ column }) => <SortableHeader column={column}>Group</SortableHeader>,
        cell: ({ row }) => <GroupCell blast={row.original} />,
        size: 180,
      })
    }

    cols.push(
      {
        id: 'Status',
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
        size: 150,
      },
      {
        id: 'Audience',
        accessorFn: (blast) => audienceSummary(blast)?.label ?? '',
        header: ({ column }) => <SortableHeader column={column}>Audience</SortableHeader>,
        cell: ({ row }) => <AudienceCell blast={row.original} />,
        size: 220,
      },
      {
        id: 'Recipients',
        accessorFn: (blast) => blast.recipients ?? -1,
        header: ({ column }) => <SortableHeader column={column}>Recipients</SortableHeader>,
        cell: ({ row }) =>
          row.original.recipients === null ? null : (
            <span className="tw:tabular-nums">{row.original.recipients.toLocaleString()}</span>
          ),
        size: 110,
        meta: { centerContent: true },
      },
      {
        id: 'Opened',
        accessorFn: (blast) => blast.openRate ?? -1,
        header: ({ column }) => (
          <HeaderWithHint hint="Unique opens, as a share of recipients. Email only, and it lands about 30 minutes after a send.">
            <SortableHeader column={column}>Opened</SortableHeader>
          </HeaderWithHint>
        ),
        cell: ({ row }) =>
          row.original.openRate === undefined ? null : (
            <span className="tw:tabular-nums">{row.original.openRate}%</span>
          ),
        size: 110,
        meta: { centerContent: true },
      },
      {
        id: 'Replies',
        accessorFn: (blast) => blast.replies ?? -1,
        header: ({ column }) => (
          <HeaderWithHint hint="Replies to this blast's own reply-to address. Email only, and only when the email inbox feature is on.">
            <SortableHeader column={column}>Replies</SortableHeader>
          </HeaderWithHint>
        ),
        cell: ({ row }) =>
          row.original.replies === undefined ? null : <span className="tw:tabular-nums">{row.original.replies}</span>,
        size: 100,
        meta: { centerContent: true },
      },
      {
        id: 'Scheduled for',
        accessorFn: (blast) => (blast.status === 'scheduled' || blast.status === 'approved' ? blast.sortKey : -1),
        header: ({ column }) => <SortableHeader column={column}>Scheduled for</SortableHeader>,
        cell: ({ row }) => {
          if (row.original.status === IN_FLIGHT) {
            return <span className="tw:whitespace-nowrap tw:text-gray-true-500">Sending now</span>
          }
          return row.original.status === 'scheduled' ? (
            <span className="tw:whitespace-nowrap tw:text-gray-700">
              {row.original.date}
              {row.original.time ? `, ${row.original.time}` : ''}
            </span>
          ) : null
        },
        size: 160,
      },
      {
        id: 'Created',
        accessorKey: 'sortKey',
        header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
        cell: ({ row }) => <span className="tw:whitespace-nowrap tw:text-gray-700">{row.original.date}</span>,
        size: 120,
      },
      {
        id: 'Actions',
        header: () => <span className="tw:sr-only">Actions</span>,
        cell: ({ row }) => <RowActions blast={row.original} />,
        enableSorting: false,
        size: 170,
        minSize: 96,
        meta: { stickyRight: true },
      }
    )

    return cols
  }, [channel, leadsManyGroups])

  const table = useReactTable({
    data: pagedBlasts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: { sorting, columnVisibility },
  })

  const layoutOptions: SegmentedOption<Layout>[] = [
    { value: 'table', label: 'Table', leadingIcon: 'table' },
    { value: 'cards', label: 'Cards', leadingIcon: 'copy-06' },
  ]

  const activeChips: Array<{ key: string; label: string; onRemove: () => void }> = [
    ...statuses.map((s) => ({
      key: `status-${s}`,
      label: STATUS_LABEL[s as Status],
      onRemove: () => setStatuses((cur) => cur.filter((x) => x !== s)),
    })),
    ...(group ? [{ key: 'group', label: group, onRemove: () => setGroup(undefined) }] : []),
    ...(search.trim() ? [{ key: 'search', label: `“${search.trim()}”`, onRemove: () => setSearch('') }] : []),
  ]

  return (
    <TooltipProvider>
      <PageLayout.Root>
        <PageLayout.Header
          heading={channel === 'email' ? 'Email blasts' : 'SMS blasts'}
          icon={channel === 'email' ? 'mail-01' : 'message-chat-square'}
          subtitle={leadsManyGroups ? undefined : GROUPS[0].name}
          actions={
            <Button rank="primary" leadingIcon="plus">
              {channel === 'email' ? 'Create new email blast' : 'Create new SMS blast'}
            </Button>
          }
        />

        <PageLayout.Body>
          <div className="tw:w-full tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:shadow-xs">
            {/* Toolbar — same shape as RosterFilterToolbar */}
            <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-3 tw:bg-white tw:p-4 tw:sm:p-6">
              <div className="tw:w-full tw:min-w-48 tw:flex-1 tw:sm:w-auto tw:sm:basis-64">
                <div className="tw:flex tw:items-center tw:rounded-lg tw:border tw:border-gray-400 tw:bg-white tw:px-3 tw:py-2 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100">
                  <span className="tw:pr-2">
                    <Icon type="search-lg" width={20} height={20} className="tw:text-gray-400" aria-hidden />
                  </span>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setPage(1)
                    }}
                    placeholder={`Search ${noun}s`}
                    aria-label={`Search ${noun}s`}
                    className="tw:w-full tw:bg-transparent tw:text-base tw:text-gray-900 tw:placeholder-gray-500 tw:focus:outline-hidden"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      aria-label="Clear search"
                      className="tw:pl-2 tw:text-gray-400 tw:hover:text-gray-600"
                    >
                      <Icon type="x-close" width={18} height={18} aria-hidden />
                    </button>
                  )}
                </div>
              </div>

              <div className="tw:w-full tw:shrink-0 tw:sm:w-40">
                <MultiSelectField
                  label="Status"
                  placeholder="All statuses"
                  showSelectedPills={false}
                  options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
                  value={statuses}
                  onValueChange={(next) => {
                    setStatuses(next)
                    setPage(1)
                  }}
                  searchPlaceholder="Find a status"
                  noResultsText="No statuses match"
                />
              </div>

              {leadsManyGroups && (
                <div className="tw:w-full tw:shrink-0 tw:sm:w-48">
                  <SelectField
                    hideLabel
                    name="group-filter"
                    label="Group"
                    emptyOptionLabel="All groups"
                    options={GROUPS.map((g) => ({ value: g.name, label: g.name }))}
                    value={group}
                    onValueChange={(v) => {
                      setGroup(v ?? undefined)
                      setPage(1)
                    }}
                  />
                </div>
              )}

              <div className="tw:flex tw:shrink-0 tw:flex-nowrap tw:items-center tw:gap-3 tw:sm:ml-auto">
                {layout === 'table' && (
                  <div className="tw:hidden tw:md:block">
                    <ColumnVisibilityDropdown table={table} nonHideableColumns={['Subject', 'Actions']} />
                  </div>
                )}
                <SegmentedControl<Layout>
                  options={layoutOptions}
                  value={layout}
                  onChange={setLayout}
                  ariaLabel="Blast layout"
                />
              </div>
            </div>

            {activeChips.length > 0 && (
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:border-t tw:border-gray-200 tw:bg-white tw:px-4 tw:py-3 tw:sm:px-6">
                {activeChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-50 tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:text-primary-700"
                  >
                    {chip.label}
                    <button
                      aria-label={`Remove filter ${chip.label}`}
                      onClick={chip.onRemove}
                      className="tw:flex tw:items-center"
                    >
                      <Icon type="x-close" className="tw:h-3 tw:w-3" aria-hidden />
                    </button>
                  </span>
                ))}
                <Button
                  rank="link"
                  size="sm"
                  leadingIcon="refresh"
                  onClick={() => {
                    setStatuses([])
                    setGroup(undefined)
                    setSearch('')
                    setPage(1)
                  }}
                >
                  Clear filters
                </Button>
              </div>
            )}

            {blasts.length === 0 ? (
              <EmptyState
                className="tw:rounded-none tw:border-x-0 tw:border-b-0 tw:bg-white"
                iconType={channel === 'email' ? 'mail-01' : 'message-chat-square'}
                headline={`No ${noun}s match your filters.`}
                subtext="Clear the search, group or status filter to see every blast."
              />
            ) : layout === 'cards' ? (
              <>
                <ul className="tw:flex tw:flex-col tw:gap-4 tw:border-t tw:border-gray-200 tw:bg-gray-cool-50 tw:p-4 tw:sm:p-6">
                  {pagedBlasts.map((blast) => (
                    <BlastCard
                      key={blast.id}
                      blast={blast}
                      channel={channel}
                      showGroup={leadsManyGroups}
                      canApprove={canApprove}
                    />
                  ))}
                </ul>
                <div className="tw:border-t tw:border-gray-200 tw:bg-gray-cool-50">
                  <DataTableFooter
                    totalCount={blasts.length}
                    pageSize={PAGE_SIZE}
                    currentPage={page}
                    label={`${noun}s`}
                  />
                </div>
              </>
            ) : (
              <DataTableRoot table={table}>
                <DataTableContent caption={`${noun}s`} className="tw:rounded-none tw:border-x-0 tw:overflow-x-auto" />
                <div className="tw:border-t tw:border-gray-200 tw:bg-gray-cool-50">
                  <DataTableFooter
                    totalCount={blasts.length}
                    pageSize={PAGE_SIZE}
                    currentPage={page}
                    label={`${noun}s`}
                  />
                </div>
              </DataTableRoot>
            )}
          </div>

          {/* GroupShow keeps the toolbar, table and footer in one card, with the
            pagination below it. Same here. */}
          <div className="tw:py-6">
            {blasts.length > 0 && (
              <StaticPagination currentPage={page} totalPages={Math.max(totalPages, 1)} onChange={setPage} />
            )}
          </div>

          {createPortal(
            <>
              <button
                onClick={() => setShowDesign((s) => !s)}
                className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
              >
                <Icon type="eye" className="tw:h-4 tw:w-4" aria-hidden />
                {showDesign ? 'Hide design tools' : 'Design tools'}
              </button>

              {showDesign && (
                <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(680px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
                  <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                    <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" aria-hidden />
                    <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                    <button
                      onClick={() => setShowDesign(false)}
                      aria-label="Hide design tools"
                      className="tw:ml-auto tw:text-gray-400 tw:hover:text-gray-600"
                    >
                      <Icon type="x-close" className="tw:h-4 tw:w-4" aria-hidden />
                    </button>
                  </div>
                  <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                    These only change the sample data, not the design.
                    <br />
                    Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
                  </p>
                  <div className="tw:flex tw:flex-wrap tw:gap-8">
                    <SegControl
                      label="Channel"
                      value={channel}
                      onChange={(v) => {
                        setChannel(v as Channel)
                        setPage(1)
                      }}
                      options={[
                        { value: 'email', label: 'Email' },
                        { value: 'sms', label: 'SMS' },
                      ]}
                    />
                    <SegControl
                      label="Viewer can approve"
                      value={canApprove ? 'yes' : 'no'}
                      onChange={(v) => setCanApprove(v === 'yes')}
                      options={[
                        { value: 'no', label: 'No (vice-captain)' },
                        { value: 'yes', label: 'Yes (captain/admin)' },
                      ]}
                    />
                    <SegControl
                      label="Groups the viewer leads"
                      value={leadsManyGroups ? 'many' : 'one'}
                      onChange={(v) => {
                        setLeadsManyGroups(v === 'many')
                        setGroup(undefined)
                        setPage(1)
                      }}
                      options={[
                        { value: 'one', label: 'One (most people)' },
                        { value: 'many', label: 'Several' },
                      ]}
                    />
                  </div>
                </div>
              )}
            </>,
            document.body
          )}
        </PageLayout.Body>
      </PageLayout.Root>
    </TooltipProvider>
  )
}

function StaticPagination({
  currentPage,
  totalPages,
  onChange,
}: {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
}) {
  const pages: Array<number | 'gap'> = []
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) pages.push(i)
    else if (pages[pages.length - 1] !== 'gap') pages.push('gap')
  }

  return (
    <div data-testid="pagination" className="tw:flex tw:items-center tw:justify-center tw:gap-4">
      <Button
        rank="link"
        size="sm"
        leadingIcon="arrow-narrow-left"
        disabled={currentPage === 1}
        onClick={() => onChange(Math.max(1, currentPage - 1))}
      >
        Previous
      </Button>

      <ul className="tw:flex tw:items-center tw:justify-center tw:gap-4">
        {pages.map((p, i) =>
          p === 'gap' ? (
            <li key={`gap-${i}`} className="tw:flex tw:items-center tw:justify-center tw:gap-2">
              <span className="tw:h-2 tw:w-2 tw:rounded-full tw:bg-gray-400" />
              <span className="tw:h-2 tw:w-2 tw:rounded-full tw:bg-gray-400" />
              <span className="tw:h-2 tw:w-2 tw:rounded-full tw:bg-gray-400" />
            </li>
          ) : (
            <li
              key={p}
              className={cn(
                'tw:h-fit tw:w-fit tw:rounded-full tw:text-xs tw:font-bold',
                p === currentPage ? 'tw:bg-primary-700 tw:text-white' : 'tw:bg-gray-100 tw:text-black'
              )}
            >
              <button
                onClick={() => onChange(p)}
                aria-current={p === currentPage ? 'page' : undefined}
                className="tw:inline-flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full"
              >
                {p}
              </button>
            </li>
          )
        )}
      </ul>

      <Button
        rank="link"
        size="sm"
        trailingIcon="arrow-narrow-right"
        disabled={currentPage === totalPages}
        onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
      >
        Next
      </Button>
    </div>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={cn(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value
                ? 'tw:bg-primary-50 tw:text-primary-700'
                : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}
